import { useState, useEffect } from 'react';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-form');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/c79d0e9f53c94e862a92cc92d9fc2574.jpg')`
        }}
      />
      
      {/* Dark Overlay Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>

      {/* Floating Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className={`text-lg sm:text-2xl font-bold transition-colors ${
            isScrolled ? 'text-[#1a2332]' : 'text-white'
          }`}>
            行政書士ARISEリーガルオフィス<span className="text-[#ff6b35]">.</span>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <a href="#services" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-[#1a2332] hover:text-[#ff6b35]' : 'text-white hover:text-[#ff6b35]'}`}>サービス内容</a>
            <a href="#reasons" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-[#1a2332] hover:text-[#ff6b35]' : 'text-white hover:text-[#ff6b35]'}`}>選ばれる理由</a>
            <a href="#faq" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-[#1a2332] hover:text-[#ff6b35]' : 'text-white hover:text-[#ff6b35]'}`}>よくある質問</a>
            <button 
              onClick={scrollToContact}
              className="px-6 py-2 bg-[#ff6b35] text-white text-sm font-medium rounded-full hover:bg-[#e55a2b] transition-all whitespace-nowrap cursor-pointer"
            >
              お問い合わせ
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-[#1a2332]' : 'text-white'
            }`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <a 
                href="#services" 
                onClick={() => scrollToSection('services')}
                className="block py-2 text-[#1a2332] hover:text-[#ff6b35] font-medium cursor-pointer"
              >
                サービス内容
              </a>
              <a 
                href="#reasons" 
                onClick={() => scrollToSection('reasons')}
                className="block py-2 text-[#1a2332] hover:text-[#ff6b35] font-medium cursor-pointer"
              >
                選ばれる理由
              </a>
              <a 
                href="#faq" 
                onClick={() => scrollToSection('faq')}
                className="block py-2 text-[#1a2332] hover:text-[#ff6b35] font-medium cursor-pointer"
              >
                よくある質問
              </a>
              <button 
                onClick={scrollToContact}
                className="w-full py-3 bg-[#ff6b35] text-white font-medium rounded-full hover:bg-[#e55a2b] transition-all cursor-pointer"
              >
                お問い合わせ
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 max-w-6xl mx-auto w-full mt-10 sm:mt-10">
        <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight tracking-wide">
          煩わしい書類の入力作成を<br />
          <span className="text-[#ff6b35]">大幅に軽減</span>します
        </h1>
        
        <p className="text-base sm:text-xl lg:text-2xl text-gray-200 mb-2 sm:mb-4 leading-relaxed">
          建設業の施工体制台帳・安全書類を行政書士がフルサポート
        </p>
        <p className="text-base sm:text-xl lg:text-2xl text-gray-200 mb-8 sm:mb-12 leading-relaxed">
          グリーンサイト対応、最短即日で提出可能
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-12 sm:mb-16">
          <button 
            onClick={scrollToContact}
            className="group relative w-full sm:w-auto h-14 sm:h-16 px-8 sm:px-12 bg-[#ff6b35] text-white text-base sm:text-lg font-bold rounded-full hover:bg-[#e55a2b] transition-all flex items-center justify-center gap-3 sm:gap-4 whitespace-nowrap cursor-pointer shadow-xl"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center">
              <i className="ri-phone-line text-[#ff6b35] text-lg sm:text-xl"></i>
            </div>
            無料相談はこちら
            <i className="ri-arrow-right-up-line text-lg sm:text-xl"></i>
          </button>
          
          <button 
            onClick={scrollToContact}
            className="w-full sm:w-auto h-14 sm:h-16 px-8 sm:px-12 bg-transparent border-2 border-white text-white text-base sm:text-lg font-bold rounded-full hover:bg-white hover:text-[#1a2332] transition-all whitespace-nowrap cursor-pointer"
          >
            見積依頼はこちら
          </button>
        </div>

        {/* Document Cards */}
        <div className="hidden sm:flex items-end justify-center gap-4 mt-20">
          <div className="w-64 h-80 bg-white rounded-2xl shadow-2xl transform -rotate-6 overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=modern%20Japanese%20construction%20site%20with%20workers%20in%20safety%20helmets%20and%20vests%2C%20organized%20building%20project%20with%20scaffolding%20and%20construction%20equipment%2C%20professional%20construction%20work%20environment%2C%20daytime%20natural%20lighting%2C%20clean%20and%20safe%20workplace%20atmosphere&width=400&height=500&seq=doc-001&orientation=portrait"
              alt="建設現場"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="w-64 h-96 bg-white rounded-2xl shadow-2xl z-10 overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=construction%20site%20organization%20chart%20document%20showing%20contractor%20hierarchy%20and%20worker%20assignments%2C%20professional%20business%20form%20with%20clean%20layout%20and%20structured%20information%20fields&width=400&height=600&seq=doc-002&orientation=portrait"
              alt="施工体制台帳"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="w-64 h-80 bg-white rounded-2xl shadow-2xl transform rotate-6 overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=green%20site%20digital%20platform%20interface%20on%20tablet%20screen%20showing%20construction%20project%20management%20dashboard%20with%20worker%20registration%20and%20safety%20document%20upload%20sections&width=400&height=500&seq=doc-003&orientation=portrait"
              alt="グリーンサイト"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
